import { Newspaper, Map, Home, Plus, Lightbulb, CircleDollarSign } from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';

export interface TabConfigItem {
  name: string;
  path: string;
  icon: LucideIcon;
  label: string;
  isCreate?: boolean;
  publicTab?: boolean;
  /** Re-summons a persistent overlay instead of routing or opening a sheet.
   *  Such a tab is never "active" — it has no route. Currently only
   *  'mapPaths' (MapPathsOverlay, the map's call-to-action block) — see
   *  uiStore.requestMapPathsOverlay and GlobalFloatingTabBar/SidebarNav's
   *  press handling. */
  overlay?: 'mapPaths';
  /** Runs an in-app action instead of routing — like `overlay`, such a tab
   *  is never "active" and has no route. 'openProperty' jumps to the user's
   *  claimed structure, or focuses the map's address search when there is
   *  none — see useOpenProperty. */
  action?: 'openProperty';
}

// Nav order and labels mirror the guidance one-to-one (client ask, Aug 2026):
// Property / Community / News, then Guidance. Community IS the map; News is
// the feed. Tab `name`s keep their historical route identities (index/feed)
// — only labels, icons, and order changed. The create tab sits mid-list so
// the phone bar centers the "+" (two destinations each side); SidebarNav
// filters it out, so the sidebar still reads P/C/N/G in order. Alerts left
// the nav (Aug 2026) for the avatar user menu — /(tabs)/alerts stays
// routable for the menu row and notification deep links.
// Funding joined the list (Sept 2026, client ask: "a $ link, on level with
// property, community, news") between News and Guidance — the row now reads
// P/C/N/Funding/G.
export const ALL_TABS: TabConfigItem[] = [
  // Jump to your claimed property, or find one via the map's address search.
  // Action tab (no route of its own) — see useOpenProperty.
  {
    name: 'property',
    path: '',
    icon: Home,
    label: 'Property',
    publicTab: true,
    action: 'openProperty',
  },
  { name: 'index', path: '/(tabs)', icon: Map, label: 'Community', publicTab: true },
  { name: 'create', path: '/(tabs)/create', icon: Plus, label: 'Create', isCreate: true },
  { name: 'feed', path: '/(tabs)/feed', icon: Newspaper, label: 'News', publicTab: true },
  {
    name: 'funding',
    path: '/(tabs)/funding',
    icon: CircleDollarSign,
    label: 'Funding',
    publicTab: true,
  },
  // Replaced the Search tab (Aug 2026, client ask). The address search bar on
  // the map covers lookup; /(tabs)/search stays routable by URL. Pressing
  // this tab re-summons MapPathsOverlay (the map's call-to-action block) —
  // it's the only guidance surface now, the old sheet is retired.
  {
    name: 'guidance',
    path: '',
    icon: Lightbulb,
    label: 'Guidance',
    publicTab: true,
    overlay: 'mapPaths',
  },
];

// Tabs the phone bar keeps in field mode (see fieldMode.ts): the rest of
// ALL_TABS stay defined and come back when the flag is off.
export const FIELD_MODE_TAB_NAMES = ['property', 'index', 'create'];

// Geometry of GlobalFloatingTabBar, shared with anything that needs to sit
// clear of it (toasts/coachmarks anchored above it, the map hover card's
// clamp math). Kept here as plain constants — the visibility-aware
// clearance itself needs hooks (useSafeAreaInsets, route segments), so that
// lives in `src/hooks/useTabBarClearance.ts` instead of pulling React into
// this plain config file.
export const TAB_BAR_HEIGHT = 52;
export const TAB_BAR_GAP = 8;
